import { useState, FC } from 'react';
import { useSnackbar } from 'notistack';
import { Stack, Box, Fade, Typography, useMediaQuery, TextField } from '@mui/material';
import { Modal, Loader, ModalSaveSection, SMBSelect } from '../../../../components';
import { IDropdownResponse, INeighborhoodLotPlan } from '../../../../models';
import {
  createBuild,
  createTestBuild,
  getNeighborhoodLotPlans,
  doesInProgressBuildExist
} from '../../../../fetch';
import { useHistory } from 'react-router-dom';
import { formatMoney } from '../../../../helpers';
import { SalesNeighborhoodDropdown } from '../../../../components/dropdowns/sales-neighborhood-dropdown';
import { getSalesNeighborhoodAvailableLots } from '../../../../fetch/lookups/sales-portal';

interface IAddBuildModal {
  open: boolean;
  onClose: (shouldRefetch?: boolean) => void;
  personId?: string;
  isTestBuild?: boolean;
  handleContractName?: (val: string) => void;
  contractName?: string;
}

export const AddBuildModal: FC<IAddBuildModal> = ({
  open,
  onClose,
  personId,
  isTestBuild,
  handleContractName,
  contractName
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const isMobile = useMediaQuery('(max-width: 900px)');
  const history = useHistory();
  const [isSaving, setIsSaving] = useState(false);
  const [showInProgressBuildError, setShowInProgressBuildError] = useState<boolean>(false);
  const [selectedNeighborhoodId, setSelectedNeighborhoodId] = useState<string | null>('');
  const [selectedLotId, setSelectedLotId] = useState<string | null>('');
  const [selectedPlanId, setSelectionPlanId] = useState<string | null>('');
  const [lots, setLots] = useState<IDropdownResponse[] | null>(null);
  const [lotPremium, setLotPremium] = useState<number | null>(null);
  const [plan, setPlan] = useState<INeighborhoodLotPlan | null>(null);
  const [plans, setPlans] = useState<INeighborhoodLotPlan[] | null>(null);
  const [isInProgressChecked, setIsInProgressChecked] = useState<boolean>(false);

  const handleClose = (shouldRefetch?: boolean) => {
    setSelectedNeighborhoodId('');
    setSelectedLotId('');
    setLots(null);
    setLotPremium(null);
    setSelectionPlanId('');
    setPlan(null);
    setPlans(null);
    setIsInProgressChecked(false);
    setShowInProgressBuildError(false);
    onClose(shouldRefetch);
  };

  const handleSave = async () => {
    if (!isInProgressChecked && !isTestBuild) {
      try {
        setIsSaving(true);
        const inProgressExists = await doesInProgressBuildExist(
          Number(selectedNeighborhoodId),
          Number(selectedLotId)
        );

        if (inProgressExists) {
          setShowInProgressBuildError(true);
          setIsInProgressChecked(true);
          return;
        } else {
          setIsInProgressChecked(true);
          setShowInProgressBuildError(false);
        }
      } catch (error) {
        enqueueSnackbar(error?.Detail ?? `Error checking in-progress build, please try again.`, {
          variant: 'error'
        });
        setIsSaving(false);
        return;
      } finally {
        setIsSaving(false);
      }
    }

    try {
      setIsSaving(true);
      const data = {
        neighborhoodId: Number(selectedNeighborhoodId),
        lotNumber: Number(selectedLotId),
        planId: plan.planId,
        planElevationId: Number(selectedPlanId),
        personId: personId ? Number(personId) : undefined,
        price: plan.price,
        contractName: contractName ?? undefined
      };
      let res = '';
      if (!isTestBuild) {
        res = await createBuild(data);
        enqueueSnackbar('New Build Created Successfully!', {
          variant: 'success'
        });
      } else {
        res = await createTestBuild(data);
        enqueueSnackbar('Test Build Created Successfully!', {
          variant: 'success'
        });
      }
      const isTestBuildList = !!contractName;
      if (isTestBuildList) {
        handleClose(true);
        return;
      }
      history.push(
        `/sales/contracting/build-options/${res}?redirect=/sales/contracting/purchasers/${personId}/builds`
      );
    } catch (error: any) {
      enqueueSnackbar(error?.Detail ?? `Error creating build, please try again.`, {
        variant: 'error'
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => handleClose()}
      maxWidth="xs"
      title={isTestBuild ? 'Create Test Build' : 'Create New Build'}
    >
      <Fade in={open}>
        <div>
          {isSaving && <Loader type="overlay" position="centered" />}
          <Box mt={1}>
            <Stack gap={1}>
              <SalesNeighborhoodDropdown
                isRequired
                value={selectedNeighborhoodId}
                handleChange={value => {
                  setSelectedNeighborhoodId(value as string);
                  setSelectedLotId('');
                  setSelectionPlanId('');
                  setPlan(null);
                  setPlans(null);
                  setIsInProgressChecked(false);
                  setShowInProgressBuildError(false);
                }}
                apiParams={{ isActive: true, hasAvailableLots: true, forAgentNewBuilds: true }}
              />
              <SMBSelect
                required
                label="Lot"
                value={selectedLotId}
                disabled={!selectedNeighborhoodId}
                handleClear={() => {
                  setSelectedLotId('');
                  setSelectionPlanId('');
                  setPlan(null);
                  setPlans(null);
                  setIsInProgressChecked(false);
                  setShowInProgressBuildError(false);
                }}
                shouldFetch={!!selectedNeighborhoodId}
                refetch={[selectedNeighborhoodId]}
                onChange={e => {
                  setSelectedLotId(e.target.value);
                  const selectedLot = lots?.filter(item => item.value === e.target.value)[0];
                  setLotPremium(selectedLot?.description ? +selectedLot?.description : null);
                  //Reset all related selected values when a new value is set
                  setSelectionPlanId('');
                }}
                apiRequest={() => getSalesNeighborhoodAvailableLots(selectedNeighborhoodId, true)}
                transformResponse={(res: IDropdownResponse[]) => {
                  setLots(res);
                  return res?.map(r => ({
                    label: `${r.value}`,
                    value: `${r.value}`
                  }));
                }}
              />
              {!!selectedLotId && !!lotPremium && (
                <Box>
                  <Typography component="span" color="primary">
                    Lot Premium:
                  </Typography>{' '}
                  <Typography color="secondary" component="span">
                    {formatMoney(lotPremium)}
                  </Typography>
                </Box>
              )}
              <SMBSelect
                required
                label="Plan"
                value={selectedPlanId}
                disabled={!selectedNeighborhoodId || !selectedLotId}
                handleClear={() => {
                  setSelectionPlanId('');
                  setPlan(null);
                  setPlans(null);
                  setIsInProgressChecked(false);
                  setShowInProgressBuildError(false);
                }}
                shouldFetch={!!selectedNeighborhoodId && !!selectedLotId}
                refetch={[selectedNeighborhoodId, selectedLotId]}
                onChange={e => {
                  setSelectionPlanId(e.target.value);
                  const selection = plans.filter(
                    option => option.planElevationId.toString() === e.target.value
                  )[0];
                  setPlan(selection);
                }}
                apiRequest={() => getNeighborhoodLotPlans(selectedNeighborhoodId, selectedLotId)}
                transformResponse={(res: INeighborhoodLotPlan[]) => {
                  setPlans(res);
                  return res.map(r => ({
                    label: `${r.planName} ${r.elevation}`,
                    value: `${r.planElevationId}`
                  }));
                }}
              />
              {!!selectedPlanId && !!plan && (
                <Box>
                  <Typography component="span" color="primary">
                    Base Price:
                  </Typography>{' '}
                  <Typography color="secondary" component="span">
                    {formatMoney(plan.price)}
                  </Typography>
                </Box>
              )}
              {showInProgressBuildError && (
                <Box>
                  <Typography component="span" color="error" noWrap={isMobile ? false : true}>
                    This Lot is currently assigned, do you wish to continue?
                  </Typography>
                </Box>
              )}
              {contractName !== undefined && (
                <TextField
                  size="small"
                  label="Contract Name"
                  autoComplete="nope"
                  required
                  variant="outlined"
                  value={contractName}
                  fullWidth
                  onChange={e => handleContractName(e.target.value)}
                />
              )}
            </Stack>
          </Box>
          <ModalSaveSection
            saveButtonText={isTestBuild ? 'Save' : 'Continue'}
            isSaveDisabled={
              isSaving ||
              !selectedNeighborhoodId ||
              !selectedPlanId ||
              !selectedLotId ||
              !plan ||
              (contractName !== undefined && !contractName)
            }
            handleCancel={() => handleClose()}
            handleSave={handleSave}
          />
        </div>
      </Fade>
    </Modal>
  );
};
